import React, { FC, useCallback, useContext, useState } from "react";
import {
  AlertMessageBanner,
  MatterClientAndMatterDetails,
  MatterTemplateEditorView,
  PricingType,
  Spinner,
  TaxStatus,
} from "@design-system";
import { UPDATE_MATTER, UPDATE_PHASE } from "../../../graphql/mutation";
import { ScopeAndAdjustmentsTabContainer } from "../../scope-and-adjustments/scope-and-adjustments-tab-container";

import { Tabs } from "antd";
import { CLIENT_MATTER_DETAILS, SCOPE_TAB, useActiveTabState } from "../../../hooks/use-active-tab";
import { useMatterTemplateEditorViewModel } from "../../../hooks/matter-template";
import { Path } from "../../../path";
import { VersionsHistoryContainer } from "../../version-history-container";
import styles from "../../matter-editor-container.css";
import { useMutation, useSettings } from "../../../hooks";
import { useQuery } from "@apollo/client";
import { doesMatterTaxStatusExcludeTax } from "../../../helpers";
import { priceOptionsToPrismaUpdate } from "../../../helpers/price-option";
import { PricingLevel } from "../../../graphql/__generated_types__/globalTypes";
import { PageLoading } from "../../../components/page-loading";
import { GlobalContext } from "~/context/global-context";
import {
  BEDROCK_SUGGEST_ASSUMPTIONS_QUERY,
  BEDROCK_SUGGEST_EXCLUSIONS_QUERY,
} from "../../../graphql/query";

const { "tab-pane": tabPaneClassname } = styles as any;

export const MatterTemplateEditorContainer: FC<{ matterTemplateId: number }> = ({
  matterTemplateId,
}) => {
  const [activeTab, setActiveTab] = useActiveTabState(
    SCOPE_TAB,
    Path.MATTER_TEMPLATE_EDITOR_BASE_PATH
  );
  const { settings } = useSettings();
  const currentUserInfo = useContext(GlobalContext);
  const useAi = currentUserInfo.enableAi;

  const {
    data: suggestedAssumptionsData,
    loading: suggestedAssumptionsLoading,
    refetch: refetchSuggestedAssumptions,
  } = useQuery(BEDROCK_SUGGEST_ASSUMPTIONS_QUERY, {
    variables: { matterId: matterTemplateId },
    skip: !useAi,
  });

  const {
    data: suggestedExclusionsData,
    loading: suggestedExclusionsLoading,
    refetch: refetchSuggestedExclusions,
  } = useQuery(BEDROCK_SUGGEST_EXCLUSIONS_QUERY, {
    variables: { matterId: matterTemplateId },
    skip: !useAi,
  });

  const [refetchingSuggestions, setRefetchingSuggestions] = useState(false);

  const handleRefetchSuggestions = useCallback(async () => {
    if (!useAi) {
      return;
    }
    setRefetchingSuggestions(true);
    try {
      await Promise.all([refetchSuggestedAssumptions(), refetchSuggestedExclusions()]);
    } finally {
      setRefetchingSuggestions(false);
    }
  }, [useAi, refetchSuggestedAssumptions, refetchSuggestedExclusions]);

  const currencySymbol = settings?.currency.symbol ?? "";
  const vm = useMatterTemplateEditorViewModel(matterTemplateId);
  const matterTemplate = vm.matterTemplate();
  const {
    pricingLevel = PricingLevel.None,
    pricingType = null,
    phases = [],
    disbursements = [],
    taxStatus = TaxStatus.None,
    organisationalUnit,
    title,
    officeLocation,
  } = matterTemplate || {};
  const matterExcludesTax = doesMatterTaxStatusExcludeTax(taxStatus);
  const alertMessage = vm.alertMessage();
  const countryCode = officeLocation?.countryCode || settings?.defaultCountryCode;

  const [updateMatter, { loading }] = useMutation(UPDATE_MATTER);

  const [updatePhase] = useMutation(UPDATE_PHASE);

  if (vm.loading()) {
    return <Spinner show={vm.loading()} message="Loading matter template..." />;
  }

  if (loading || !settings) return <Spinner show />;
  const tabs: any[] = [
    <Tabs.TabPane tab="Template Details" key={CLIENT_MATTER_DETAILS}>
      <MatterClientAndMatterDetails
        onDelete={async () => {
          const id = matterTemplate?.id;
          if (id) {
            await updateMatter({
              variables: {
                id,
                data: {
                  deletedAt: { set: new Date() },
                  deletedBy: {
                    connect: { id: currentUserInfo?.currentUser?.userId },
                  },
                },
              },
            });
          }
        }}
        template={"template"}
        matter={matterTemplate as any}
        pmsName={settings.organisationPmsName || ""}
        practiceAreas={vm.practiceAreas() || []}
        onMatterUpdate={async (matterData) => {
          const { id, practiceAreaId: organisationalUnitId } = matterData;
          await updateMatter({
            variables: {
              id,
              data: {
                title: { set: matterData.title },
                organisationalUnit: { connect: { id: organisationalUnitId } },
              },
            },
          });
        }}
        feeEarnersCascaderRender={() => null}
      />
    </Tabs.TabPane>,
    <Tabs.TabPane tab="Scope & Adjustments" key={SCOPE_TAB}>
      <ScopeAndAdjustmentsTabContainer
        title={title}
        clientName={""}
        currencySymbol={currencySymbol}
        countryCode={countryCode ?? undefined}
        template={true}
        matterId={matterTemplateId}
        phases={phases}
        pricingLevel={pricingLevel}
        pricingType={pricingType || PricingType.None}
        organisationalUnitId={organisationalUnit?.id ?? 0}
        disbursements={disbursements}
        excludeTax={matterExcludesTax}
        taxLabel={settings.taxLabel}
        taxLevelPercent={settings?.taxLevelPercent ?? 0}
        uplifts={[]}
        discounts={[]}
        practiceAreas={vm.practiceAreas() || []}
        aiGeneratedMatterSummary={null}
        suggestedAssumptions={
          useAi && !refetchingSuggestions
            ? suggestedAssumptionsData?.bedrockSuggestAssumptions || undefined
            : undefined
        }
        suggestedAssumptionsLoading={
          !!useAi && (suggestedAssumptionsLoading || refetchingSuggestions)
        }
        suggestedExclusions={
          useAi && !refetchingSuggestions
            ? suggestedExclusionsData?.bedrockSuggestExclusions || undefined
            : undefined
        }
        suggestedExclusionsLoading={
          !!useAi && (suggestedExclusionsLoading || refetchingSuggestions)
        }
        handleRefetchSuggestions={useAi ? handleRefetchSuggestions : undefined}
        setTopDownRefreshTrigger={undefined}
      />
    </Tabs.TabPane>,
  ];

  return (
    <>
      {alertMessage && <AlertMessageBanner banner {...alertMessage} />}
      <MatterTemplateEditorView
        currencySymbol={currencySymbol}
        taxLabel={settings.taxLabel}
        taxLevelPercent={settings?.taxLevelPercent ?? 0}
        excludeTax={matterExcludesTax}
        pricingSummaries={vm.pricingOptions()}
        matterTemplate={matterTemplate as any}
        tabs={tabs}
        onChosenPriceChange={(value) => vm.setPriceOptions(pricingType!, value || 0)}
        onChosenRangePriceChange={(value) => vm.setPriceOptions(PricingType.EstimatedRange, value)}
        onTitleChange={(title) => vm.setTitle(title)}
        activeTab={activeTab}
        onChangeTab={setActiveTab}
        pricingSummaryFooter={<> </>}
        settings={settings}
        versionsSidebar={<VersionsHistoryContainer matterId={matterTemplateId} />}
        onPhasePriceChange={(phaseId, priceDetails) => {
          return updatePhase({
            variables: {
              data: { priceOptions: priceOptionsToPrismaUpdate(priceDetails) as any },
              phaseId: phaseId,
              matterId: matterTemplateId,
            },
          });
        }}
      />
    </>
  );
};
